import { createFileRoute } from '@tanstack/react-router';

import { PageHeader } from '@/components/PageHeader';
import { MainContainer } from '@/components/MainContainer';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Box, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { endOfMonth } from 'date-fns';
import {
  BillingHistoryBar,
  BillingHistoryCumSumLine,
  TokenHistoryCumSumLine,
} from '@/components/Charts/TokenHistoryBar';
import { MonthSelector } from '@/components/Charts/MonthSelector';
import { fmtCurrency } from '@/lib/currency';
import { transformChartData } from '@/lib/tokenUsage';
import { isPersonalTenant } from '@/lib/auth';
import { MonthlySpend, WebSearchUsage } from '@/components/UsageMeter';

export const Route = createFileRoute('/_main/_project/project/usage/')({
  component: () => <Component />,
});

const DEBUG = false;

function Component() {
  const { tenant, project, projectId, adminClient, userInfo } = Route.parentRoute.useLoaderData();
  const currency = tenant.spec.billing_settings.currency;
  // 0 は上限なしを意味するので、金額としては表示しない
  const fmtLimit = (v: number | null | undefined) => (v ? fmtCurrency(v, currency) : null);

  const isArchived = project?.status === 'archived';

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const tokenHistoryQuery = useQuery({
    queryKey: ['projectTokenHistory', { projectId, year, month }],
    queryFn: async () => {
      let { items, currency } = await adminClient.settings_v1_get_project_token_history({
        params: {
          project_id: projectId!,
        },
        queries: {
          year,
          month,
        },
      });
      if (DEBUG) {
        items = makeTestdata(month);
        currency = 'USD';
      }

      const { records, models } = transformChartData(year, month, items);

      return {
        items: records,
        currency,
        models,
      };
    },
  });

  return (
    <MainContainer>
      <PageHeader title="利用状況" />
      {isArchived && <Text c="red">このプロジェクトはアーカイブされています。</Text>}
      {!projectId && <Text>右上のプロジェクト選択から、プロジェクトを選択してください。</Text>}
      {projectId && !project && (
        <Text>
          プロジェクトが見つかりませんでした。
          <br />
          右上のプロジェクト選択から、プロジェクトを選択してください。
        </Text>
      )}
      {projectId && project && (
        <Stack>
          {!isPersonalTenant(userInfo) && (
            <>
              <Title order={5}>プロジェクト利用上限</Title>
              <p>{fmtLimit(project?.usage_limits?.limit_monthly_bill) ?? '上限なし'}</p>
            </>
          )}
          <MonthlySpend
            limit={project.usage_limits?.limit_monthly_bill}
            currency={currency}
            queryKey={['projectMonthlySpend', { projectId }]}
            fetchItems={async (year, month) =>
              (
                await adminClient.settings_v1_get_project_token_history({
                  params: { project_id: projectId },
                  queries: { year, month },
                })
              ).items
            }
          />
          {isPersonalTenant(userInfo) && <WebSearchUsage adminClient={adminClient} />}

          <Title order={5} mt="xl">
            プロジェクト利用状況
          </Title>
          <MonthSelector year={year} setYear={setYear} month={month} setMonth={setMonth} />
          {tokenHistoryQuery.isLoading && (
            <Skeleton visible w="100%" h="50vh" mt="xl" opacity="30%" />
          )}
          {tokenHistoryQuery.isError && <div>Error</div>}
          {tokenHistoryQuery.data && (
            <>
              <Box mt="xl">
                <Title order={4}>利用額</Title>
                <BillingHistoryBar
                  data={tokenHistoryQuery.data.items}
                  year={year}
                  month={month}
                  currency={tokenHistoryQuery.data.currency}
                />
              </Box>
              <Box>
                <Title order={4}>利用額（累積）</Title>
                <BillingHistoryCumSumLine
                  data={tokenHistoryQuery.data.items}
                  year={year}
                  month={month}
                  currency={tokenHistoryQuery.data.currency}
                />
              </Box>
              <Title order={4}>API・モデルごとのトークン消費量</Title>
              {tokenHistoryQuery.data.models.map(([service, model]) => (
                <Box key={`${service}-${model}`}>
                  <Title order={5}>
                    {service} （モデル: {model}）
                  </Title>
                  <Group gap="0" justify="space-between">
                    <Box w="50%">
                      <TokenHistoryCumSumLine
                        service={service}
                        model={model}
                        data={tokenHistoryQuery.data.items}
                        inputOrOutput="input_tokens"
                        year={year}
                        month={month}
                      />
                    </Box>
                    <Box w="50%">
                      <TokenHistoryCumSumLine
                        service={service}
                        model={model}
                        data={tokenHistoryQuery.data.items}
                        inputOrOutput="output_tokens"
                        year={year}
                        month={month}
                      />
                    </Box>
                  </Group>
                </Box>
              ))}
            </>
          )}
        </Stack>
      )}
    </MainContainer>
  );
}

const makeTestdata = (month: number) => {
  return Array.from({ length: endOfMonth(new Date(2024, month - 1)).getDate() }, (_, i) => ({
    start_time: new Date(2024, month - 1, i + 1).toISOString(),
    end_time: new Date(2024, month - 1, i + 2).toISOString(),
    bill_spent: Math.random() * 1000,
    usage: {},
  }));
};
